import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { TextField, Button, Select, MenuItem, Chip, FormControl, Checkbox, FormControlLabel, Slider, IconButton } from '@mui/material';
import { Users, Send, AlertTriangle, Zap, Target, MessageSquare, Phone, CheckCircle, AlertCircle, Tag, MapPin, Globe, Calendar, Clock, Plus, XIcon, AtSign, ArrowLeft } from 'lucide-react';
import Navbar from '../../../components/Navbar';
import BlastModal from '../../../components/Campaigns/BlastModal';
import TestSmsModal from '../../../components/Campaigns/TestSmsModal';
import BlastProcessing from '../../../components/Campaigns/BlastProcessing';
import { useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {toast} from "react-toastify";
import { campaignsRequest } from '../../../requests/vendor/CampaignsRequest';
import SendersRequest from '../../../requests/vendor/SendersRequest';
import VendorRequests from '../../../requests/vendor/VendorRequests';
import { useSelector } from 'react-redux';
import SubscriptionPrompt from '../../../components/SubscriptionPrompt';

const BlastCampaign = () => {
  const user = useSelector((state) => state?.user?.user);
  const subscription = user?.subscription;
  const plan = subscription?.subscription_plan;
  const [showSubscriptionPrompt, setShowSubscriptionPrompt] = useState(false);

  const [testPhoneNumbers, setTestPhoneNumbers] = useState(['']);
  const [customBlastCount, setCustomBlastCount] = useState('');
  const [blastToAll, setBlastToAll] = useState(false);
  const [blastHistory, setBlastHistory] = useState({
    totalSent: 2420,
    remainingSubscribers: 3000,
    remainingFiltered: 2000
  });
  const [isBlastModalOpen, setIsBlastModalOpen] = useState(false);
  const [selectedSender, setSelectedSender] = useState('');
  const [isProcessingModalOpen, setIsProcessingModalOpen] = useState(false);
  const [processingRecipientCount, setProcessingRecipientCount] = useState(0);
  const [isTestSmsModalOpen, setIsTestSmsModalOpen] = useState(false);
  const [campaignData, setCampaignData] = useState(null);
  const [campaignLoading, setCampaignLoading] = useState(true);
  const [senders, setSenders] = useState([]);
  const [error, setError] = useState(null);
  const {campaign_slug} = useParams();
  const [showAllCategories, setShowAllCategories] = useState(false);
  const [totalCampaignSubscribers, setTotalCampaignSubscribers] = useState(0);
  const [initialBlast, setInitialBlast] = useState(0);
  const [blastSuccess, setBlastSuccess] = useState(false);
  const [totalSmsLeads, setTotalSmsLeads] = useState(0);
  const [userPlan, setUserPlan] = useState(plan?.name);
  const navigate = useNavigate();


  const PLACEHOLDER_IMAGE = "https://placehold.co/600x400/e2e8f0/64748b?text=No+Image+Available";
  const getPlanPerAmount = (plan)=>{
    const plan_name = plan?.name?.toLowerCase();
    switch(plan_name){
      case "basic plan":
        return parseFloat(0.066)
      case "pro plan":
        return parseFloat(0.0568)
      case "enterprise plan":
        return parseFloat(0.0416)
      default:
        return parseFloat(0.05)
    }
  }

  //* Combine API calls into a single useEffect for better performance
  useEffect(() => {
    let isMounted = true;

    const fetchData = async () => {
      try {
        setCampaignLoading(true);
        setError(null);

        //* Fetch both campaign and sender data in parallel
        const [campaignResponse, sendersResponse, vendorPlansDetailsResponse] = await Promise.all([
          campaignsRequest.getCampaignById(campaign_slug),
          SendersRequest.getAllSenders(),
          VendorRequests.getVendorPlansDetails(),
        ])
        if (!isMounted) return;
        
        //*  Handle campaign data
        if (campaignResponse.in_error === false && campaignResponse.status_code === "000") {
          setCampaignData(campaignResponse.data[0]);
          setTotalCampaignSubscribers(campaignResponse.data[0].subscriber_count);
          setInitialBlast(campaignResponse.data[0].initial_blast);
        } else {
          setError("Failed to fetch campaign details");
          toast.error("Error fetching campaign details");
        }

        //* Handle senders data
        if (sendersResponse.data?.in_error === false && sendersResponse.data?.status_code === "000") {
          const approvedSenders = sendersResponse.data?.data.filter(sender =>  sender.status?.toLowerCase() === "approved")
          setSenders(approvedSenders);
        } else {
          toast.error("Sorry, Sender Ids could not be fetched");
        }

        //* Handle vendor plans details
        if (vendorPlansDetailsResponse.data?.in_error === false && vendorPlansDetailsResponse.data?.status_code === "000") {
          setTotalSmsLeads(vendorPlansDetailsResponse.data?.data?.total_sms_leads);
        } else {
          toast.error("Sorry, Vendor plans details could not be fetched");
        }
      } catch (error) {
        if (!isMounted) return;
        console.error("Error fetching data:", error);
        setError("Failed to fetch required data");
        toast.error("Error loading required data");
      } finally {
        if (isMounted) {
          setCampaignLoading(false);
        }
      }
    };

    fetchData();

    return () => {
      isMounted = false;
    };
  }, [campaign_slug]);

  if (campaignLoading) {
    return (
      <div className="flex justify-center items-center min-h-screen bg-gradient-to-br from-gray-50 to-gray-100">
        <div className="text-center">
          <div className="mx-auto w-12 h-12 rounded-full border-b-2 border-[#ff5555] animate-spin"></div>
          <p className="mt-4 text-gray-600">Loading campaign data...</p>
        </div>
      </div>
    );
  }

  // Show error state
  if (error) {
    return (
      <div className="flex justify-center items-center min-h-screen bg-gradient-to-br from-gray-50 to-gray-100">
        <div className="p-8 text-center bg-white rounded-xl shadow-sm">
          <AlertTriangle className="mx-auto mb-4 w-12 h-12 text-red-500" />
          <h2 className="mb-2 text-xl font-semibold text-gray-900">Error Loading Data</h2>
          <p className="text-gray-600">{error}</p>
          <Button
            variant="contained"
            onClick={() => window.location.reload()}
            sx={{
              marginTop: '1rem',
              backgroundColor: '#FF6C6C',
              '&:hover': { backgroundColor: '#ff5555' }
            }}
          >
            Retry
          </Button>
        </div>
      </div>
    );
  }

  const senderOptions = senders.map(sender => ({
    id: sender.id,
    name: sender.sender
  }));

  //* Update the deal preview section to use campaignData
  const dealPreview = campaignData ? {
    title: campaignData.title,
    price: campaignData.price,
    discountPrice: campaignData.discounted_price,
    image: campaignData.image,
    categories: campaignData.category || [],
    redeemLocations: [...new Set(campaignData.location?.map(loc => loc.location))] || [],
    redeemRegions: [...new Set(campaignData.location?.map(loc => loc.region))] || [],
    redeemOptions: campaignData.redeem_options || [],
    created_at: campaignData.start_date,
    expiry_date: campaignData.end_date
  } : null;
  console.log("dealPreview", dealPreview)

  const calculateMetrics = () => {
    const totalSubscribers = totalCampaignSubscribers || 0;
    const initialBlastCount = initialBlast || 0;
    const remainingToBlastCount = Math.max(0, totalSubscribers - initialBlastCount);
    const possibleBlasts = Math.min(totalSmsLeads, remainingToBlastCount);
    const insufficientCredits = totalSmsLeads < remainingToBlastCount;

    return {
      totalSubscribers,
      filteredSubscribers: totalSubscribers,
      remainingToBlast: remainingToBlastCount,
      possibleBlasts,
      insufficientCredits,
      coverage: totalSubscribers > 0 ? ((totalSubscribers - remainingToBlastCount) / totalSubscribers) * 100 : 0,
      estimatedCost: possibleBlasts * getPlanPerAmount(userPlan)
    };
  };

  const metrics = calculateMetrics();

  const formatPhoneNumber = (number) => {
    // Remove any non-digit characters
    const cleaned = number.replace(/\D/g, '');
    
    // If number starts with 0, replace with 233
    if (cleaned.startsWith('0')) {
      return '233' + cleaned.slice(1);
    }
    
    // If number doesn't start with 233, add it
    if (!cleaned.startsWith('233')) {
      return '233' + cleaned;
    }
    
    return cleaned;
  };

  const getFormattedPhoneNumbers = () => {
    return testPhoneNumbers
      .filter(num => num.trim() !== '') // Remove empty numbers
      .map(num => formatPhoneNumber(num))
      .join(',');
  };

  const getFormattedPhoneNumbersArray = () => {
    return testPhoneNumbers
      .filter(num => num.trim() !== '') // Remove empty numbers
      .map(num => formatPhoneNumber(num));
  };

  const handleTestNumberChange = (index, value) => {
    const newNumbers = [...testPhoneNumbers];
    newNumbers[index] = value;
    setTestPhoneNumbers(newNumbers);
  };

  const handleTestSmsConfirm = () => {
    if (isFreeTrial()) {
      setShowSubscriptionPrompt(true);
      return;
    }

    const formattedNumbers = getFormattedPhoneNumbers();
    const testRecipientCount = testPhoneNumbers.length;
    setProcessingRecipientCount(testRecipientCount);
    setIsTestSmsModalOpen(false);
    setIsProcessingModalOpen(true);
  };

  // Add this new function to reset phone numbers after successful test SMS
  const resetTestPhoneNumbers = () => {
    setTestPhoneNumbers(['']);
  };

  const handleAddTestNumber = () => {
    if (testPhoneNumbers.length < 5) {
      setTestPhoneNumbers([...testPhoneNumbers, '']);
    }
    
  };

  const handleRemoveTestNumber = (index) => {
    const newNumbers = testPhoneNumbers.filter((_, i) => i !== index);
    setTestPhoneNumbers(newNumbers);
  };

  const isValidBlastCount = () => {
    if (blastToAll) return true;
    const count = parseInt(customBlastCount);
    return count > 0 && count <= metrics.possibleBlasts;
  };

  const handleBlastConfirm = async () => {
    if (isFreeTrial()) {
      setShowSubscriptionPrompt(true);
      return;
    }

    try {
      setIsBlastModalOpen(false);
      setIsProcessingModalOpen(true);
      setBlastSuccess(false);
      
      const recipientCount = blastToAll 
        ? Math.min(metrics.possibleBlasts, metrics.filteredSubscribers)
        : parseInt(customBlastCount);
      
      setProcessingRecipientCount(recipientCount);
      
      const response = await campaignsRequest.blastCampaign(
        selectedSender,
        recipientCount,
        campaign_slug
      );

      if (response.data?.status_code === "000" && response.data?.in_error === false) {
        setBlastSuccess(true);
        toast.success('Campaign blast sent successfully!');
      } else {
        throw new Error(response.data?.reason || 'Failed to send campaign blast');
      }
    } catch (error) {
      console.error('Blast campaign error:', error);
      toast.error(error.message || 'Failed to initiate campaign blast');
      setIsProcessingModalOpen(false);
    }
  };

  // Add this function to check if user is on free trial
  const isFreeTrial = () => {
    return plan?.name?.toLowerCase() === "free trial";
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 to-gray-100">
      <Navbar page={"Blast Selected Campaign"} />
      
      <div className="px-4 py-4 sm:px-6 sm:py-8">
        <div className="mx-auto max-w-7xl">
          {/* Back Button */}
          <motion.div
            initial={{ opacity: 0, x: -20 }}
            animate={{ opacity: 1, x: 0 }}
            className="mb-4"
          >
            <Button
              onClick={() => navigate(-1)}
              startIcon={<ArrowLeft className="w-4 h-4" />}
              sx={{
                color: '#64748B',
                backgroundColor: 'white',
                borderRadius: '12px',
                boxShadow: '0 1px 2px rgba(0, 0, 0, 0.05)',
                '&:hover': {
                  backgroundColor: '#F8FAFC',
                },
                textTransform: 'none',
                fontWeight: 500,
              }}
            >
              Back to Campaigns
            </Button>
          </motion.div>

          <motion.div 
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
            className="p-4 mb-6 bg-white rounded-2xl shadow-sm sm:p-8 sm:mb-8"
          >
            <div className="flex flex-col space-y-4 sm:flex-row sm:justify-between sm:items-center sm:space-y-0">
              <div className="flex gap-4 items-center">
                <div className="p-3 bg-indigo-50 rounded-xl">
                  <Send className="w-6 h-6 text-indigo-600 sm:w-8 sm:h-8" />
                </div>
                <div>
                  <h1 className="text-2xl font-bold text-gray-900 sm:text-2xl">SMS Blast Campaign</h1>
                  <p className="mt-1 text-sm text-gray-500 sm:text-base">Target and reach your audience effectively</p>
                </div>
              </div>
              <div className="flex gap-3 items-center sm:gap-6">
                <div className="flex-1 px-4 py-2 text-center bg-indigo-50 rounded-xl sm:px-6 sm:py-3 sm:flex-none">
                  <p className="text-xs text-gray-600 sm:text-sm">SMS Leads</p>
                  <p className="text-xl font-bold text-indigo-600 sm:text-2xl">{totalSmsLeads}</p>
                </div>
                <div className="flex-1 px-4 py-2 text-center bg-green-50 rounded-xl sm:px-6 sm:py-3 sm:flex-none">
                  <p className="text-xs text-gray-600 sm:text-sm">Cost per SMS</p>
                  <p className="text-xl font-bold text-green-600 sm:text-2xl">GHC {getPlanPerAmount(userPlan)}</p>
                </div>
              </div>
            </div>
          </motion.div>

          <div className="grid grid-cols-1 gap-2 lg:grid-cols-12 sm:gap-6 lg:gap-8">
            <div className="space-y-4 lg:col-span-7 sm:space-y-6">
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                className="p-4 bg-white rounded-2xl border border-gray-100 shadow-sm sm:p-8"
              >
                <div className="flex gap-3 items-center mb-6">
                  <div className="p-3 bg-purple-50 rounded-xl">
                    <Tag className="w-6 h-6 text-purple-500" />
                  </div>
                  <h2 className="text-xl font-semibold">Deal Preview</h2>
                </div>

                <div className="overflow-hidden relative p-4 bg-gradient-to-br from-purple-50 to-indigo-50 rounded-xl sm:p-6">
                  <div className="flex flex-col gap-4 sm:flex-row sm:gap-8">
                    <img 
                      src={dealPreview && (dealPreview.image || PLACEHOLDER_IMAGE)} 
                      alt={dealPreview && (dealPreview.title || "Deal image")}
                      className="object-cover w-full h-48 rounded-xl shadow-lg sm:w-48"
                    />
                    <div className="flex-1 space-y-4">
                      <div>
                        <h3 className="text-lg font-bold text-gray-900">{dealPreview && dealPreview.title}</h3>
                        
                        {/* Location display */}
                        {/* <div className="flex gap-2 items-center mt-2 text-gray-600">
                          <MapPin className="w-4 h-4" />
                          <span className="text-sm">
                            {dealPreview && dealPreview.redeemLocations ? 
                              (dealPreview.redeemLocations.includes("all") || dealPreview.redeemLocations.includes("All") || 
                                dealPreview.redeemRegions.includes("all") || dealPreview.redeemRegions.includes("All")
                                ? "Nationwide"
                                : dealPreview.redeemLocations.filter(loc => loc.toLowerCase() !== "all").join(", "))
                              : "No locations specified"
                            }
                          </span>
                        </div> */}
                        
                        {/* Pricing section */}
                        <div className="p-4 mt-4 rounded-xl border border-gray-100 backdrop-blur-sm bg-white/80">
                          <div className="flex flex-col gap-2">
                            <div className="flex gap-1 items-center">
                              <span className="font-bold text-green-600 text-md">
                                ₵{dealPreview && dealPreview.discountPrice?.toLocaleString()}
                              </span>
                              <span className="text-gray-400 line-through text-md">
                                ₵{dealPreview && dealPreview.price?.toLocaleString()}
                              </span>
                            </div>
                            <div className="inline-flex items-center px-1 py-1 bg-green-50 rounded-full w-fit">
                              <span className="text-sm font-medium text-green-700">
                                Save GHC {dealPreview && (dealPreview.price - dealPreview.discountPrice)?.toLocaleString()}
                              </span>
                            </div>
                            <div className="text-xs text-gray-500">
                              {dealPreview && Math.round(((dealPreview.price - dealPreview.discountPrice) / dealPreview.price) * 100)}% OFF
                            </div>
                          </div>
                        </div>
                      </div>
                      
                      {/* <div className="flex flex-wrap gap-2">
                        {dealPreview && dealPreview.categories.map((category) => (
                          <span key={category} className="px-4 py-1.5 text-sm font-medium text-purple-700 rounded-full shadow-sm bg-white/80">
                            {category}
                          </span>
                        ))}
                      </div> */}
                    </div>
                  </div>
                </div>
              </motion.div>

              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                className="p-4 bg-white rounded-2xl shadow-sm sm:p-8"
              >
                <div className="space-y-6 sm:space-y-8">

                  <div className="grid grid-cols-1 gap-4 sm:grid-cols-3 sm:gap-6">
                    {/* Categories Card */}
                    <div className="p-4 bg-purple-50 rounded-xl border border-purple-100 sm:p-5">
                      <div className="flex gap-2 items-center mb-3">
                        <Tag className="w-5 h-5 text-purple-600" />
                        <h4 className="font-semibold text-purple-900">Categories</h4>
                      </div>
                      <div className="flex flex-wrap gap-2">
                        {dealPreview && dealPreview.categories
                          .slice(0, showAllCategories ? undefined : 3)
                          .map((category) => (
                            <Chip
                              key={category}
                              label={category}
                              variant="outlined"
                              className="text-purple-700 border-purple-200 bg-white/80"
                            />
                        ))}
                        {dealPreview && dealPreview.categories.length > 3 && (
                          <Button
                            variant="text"
                            size="small"
                            onClick={() => setShowAllCategories(!showAllCategories)}
                            sx={{
                              color: '#9333EA',
                              fontSize: '0.875rem',
                              padding: '2px 8px',
                              minWidth: 'auto',
                              '&:hover': {
                                backgroundColor: 'rgba(147, 51, 234, 0.04)'
                              }
                            }}
                          >
                            {showAllCategories ? (
                              'Show Less'
                            ) : (
                              `+${dealPreview.categories.length - 3} more`
                            )}
                          </Button>
                        )}
                      </div>
                    </div>

                    {/* Locations Card */}
                    <div className="p-4 bg-blue-50 rounded-xl border border-blue-100 sm:p-5">
                      <div className="flex gap-2 items-center mb-3">
                        <MapPin className="w-5 h-5 text-blue-600" />
                        <h4 className="font-semibold text-blue-900">Locations</h4>
                      </div>
                      <div className="flex flex-wrap gap-2">
                        {dealPreview && dealPreview.redeemLocations.includes("all") || dealPreview.redeemLocations.includes("All") ? (
                          <Chip
                            key="nationwide"
                            label="Nationwide"
                            variant="outlined"
                            className="text-blue-700 border-blue-200 bg-white/80"
                          />
                        ) : (
                          dealPreview && dealPreview.redeemLocations.map((location) => (
                            <Chip
                              key={location}
                              label={location}
                              variant="outlined"
                              className="text-blue-700 border-blue-200 bg-white/80"
                            />
                          ))
                        )}
                      </div>
                    </div>

                    {/* Regions Card */}
                    <div className="p-4 bg-green-50 rounded-xl border border-green-100 sm:p-5">
                      <div className="flex gap-2 items-center mb-3">
                        <Globe className="w-5 h-5 text-green-600" />
                        <h4 className="font-semibold text-green-900">Regions</h4>
                      </div>
                      <div className="flex flex-wrap gap-2">
                        {dealPreview && dealPreview.redeemRegions.includes("all") || dealPreview.redeemRegions.includes("All") ? (
                          <Chip
                            key="nationwide"
                            label="Nationwide"
                            variant="outlined"
                            className="text-green-700 border-green-200 bg-white/80"
                          />
                        ) : (
                          dealPreview && dealPreview.redeemRegions.map((region) => (
                            <Chip
                              key={region}
                              label={region}
                              variant="outlined"
                              className="text-green-700 border-green-200 bg-white/80"
                            />
                          ))
                        )}
                      </div>
                    </div>
                  </div>

                  {/* Campaign Duration - Full width on mobile */}
                  <div className="grid grid-cols-1 gap-4 p-4 bg-gradient-to-br rounded-xl border border-indigo-100 sm:grid-cols-2 sm:gap-6 sm:p-6 from-indigo-50/50 to-purple-50/50">
                    <div className="space-y-2">
                      <p className="text-sm font-medium text-gray-600">Campaign Period</p>
                      <div className="flex gap-3 items-center">
                        <Calendar className="w-5 h-5 text-indigo-600" />
                        <p className="text-xs font-medium text-gray-900">
                          {new Date(dealPreview && dealPreview.created_at).toLocaleDateString()} - {new Date(dealPreview && dealPreview.expiry_date).toLocaleDateString()}
                        </p>
                      </div>
                    </div>
                    <div className="space-y-2">
                      <p className="text-sm font-medium text-gray-600">Duration</p>
                      <div className="flex gap-3 items-center">
                        <Clock className="w-5 h-5 text-purple-600" />
                        <p className="text-xs font-medium text-gray-900">
                          {Math.ceil((new Date(dealPreview && dealPreview.expiry_date) - new Date(dealPreview && dealPreview.created_at)) / (1000 * 60 * 60 * 24))} Days
                        </p>
                      </div>
                    </div>
                  </div>
                </div>
              </motion.div>

              {/* Test SMS Panel - Redesigned for better mobile */}
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.1 }}
                className="p-4 bg-white rounded-2xl border-2 border-green-100 shadow-sm sm:p-8"
              >
                {/* Header Section */}
                <div className="flex flex-col gap-4 mb-6">
                  <div className="flex gap-4 items-start sm:items-center">
                    <div className="p-3 bg-green-50 rounded-xl shrink-0">
                      <Phone className="w-6 h-6 text-green-500" />
                    </div>
                    <div className="flex-1">
                      <h2 className="text-xl font-semibold text-gray-900">Test Your SMS Campaign</h2>
                      <p className="text-sm text-gray-500">Send a test message before blasting to all subscribers</p>
                    </div>
                  </div>
                  <div className="self-start px-4 py-2 w-full bg-green-50 rounded-lg sm:w-auto">
                    <span className="text-sm font-medium text-green-700">
                      {5 - testPhoneNumbers.length} test numbers remaining
                    </span>
                  </div>
                </div>

                <div className="space-y-4">
                  {/* Info Alert */}
                  <div className="p-4 rounded-xl border border-green-100 bg-green-50/50">
                    <div className="flex gap-2 items-start sm:items-center">
                      <AlertCircle className="mt-0.5 w-5 h-5 text-green-600 shrink-0 sm:mt-0" />
                      <p className="text-sm font-medium text-green-700">
                        Add up to 5 phone numbers to test your SMS campaign
                      </p>
                    </div>
                  </div>

                  {/* Sender ID Selection */}
                  {/* Phone Number Inputs */}
                  <div className="space-y-3">
                    {testPhoneNumbers.map((number, index) => (
                      <div key={index} className="flex gap-2 items-center">
                        <TextField
                          label={`Phone ${index + 1}`}
                          value={number}
                          onChange={(e) => handleTestNumberChange(index, e.target.value)}
                          variant="outlined"
                          fullWidth
                          placeholder="Enter phone number"
                          size="small"
                          sx={{
                            '& .MuiOutlinedInput-root': {
                              borderRadius: '12px',
                              '&:hover fieldset': { borderColor: '#22C55E' },
                              '&.Mui-focused fieldset': { borderColor: '#22C55E' }
                            },
                            '& .MuiInputLabel-root.Mui-focused': { color: '#22C55E' }
                          }}
                        />
                        <IconButton 
                          onClick={() => handleRemoveTestNumber(index)}
                          className="p-2 shrink-0 hover:bg-red-50"
                          sx={{
                            color: '#EF4444',
                            '&:hover': { backgroundColor: '#FEE2E2' }
                          }}
                        >
                          <XIcon className="w-5 h-5" />
                        </IconButton>
                      </div>
                    ))}
                  </div>
                  
                  {/* Action Buttons */}
                  <div className="flex flex-col gap-3 justify-between items-stretch pt-4 sm:flex-row sm:items-center">
                    <div className="flex-1">
                      {testPhoneNumbers.length < 5 && (
                        <Button
                          variant="text"
                          onClick={handleAddTestNumber}
                          startIcon={<Plus className="w-4 h-4" />}
                          fullWidth
                          sx={{
                            color: '#22C55E',
                            justifyContent: 'flex-start',
                            padding: '8px 16px',
                            '&:hover': {
                              backgroundColor: '#F0FDF4'
                            }
                          }}
                        >
                          Add Another Number
                        </Button>
                      )}
                    </div>
                    <Button
                      variant="contained"
                      disabled={!testPhoneNumbers[0]}
                      endIcon={<Send className="w-4 h-4" />}
                      onClick={() => setIsTestSmsModalOpen(true)}
                      sx={{
                        backgroundColor: '#22C55E',
                        borderRadius: '12px',
                        padding: '10px 24px',
                        minWidth: '160px',
                        '&:hover': {
                          backgroundColor: '#16A34A'
                        },
                        '&.Mui-disabled': {
                          backgroundColor: '#E5E7EB',
                          color: '#9CA3AF'
                        }
                      }}
                    >
                      Send Test SMS
                    </Button>
                  </div>
                </div>
              </motion.div>
            </div>

            {/* Right Column - Stats & Actions (5 columns) */}
            <div className="space-y-4 lg:col-span-5 sm:space-y-6">
              {/* Metrics Card - Redesigned */}
              <motion.div
                initial={{ opacity: 0, x: 20 }}
                animate={{ opacity: 1, x: 0 }}
                className="bg-white rounded-2xl shadow-sm lg:sticky lg:top-6"
              >
                <div className="p-4 border-b border-gray-100 sm:p-8">
                  <div className="flex gap-3 items-center">
                    <div className="p-2 bg-indigo-50 rounded-lg">
                      <Users className="w-5 h-5 text-indigo-600" />
                    </div>
                    <h3 className="text-xl font-semibold">Campaign Summary</h3>
                  </div>
                </div>

                <div className="p-4 space-y-6 sm:p-8 sm:space-y-8">
                  {/* Credit Status */}
                  <div className={`p-4 rounded-xl ${
                    metrics.insufficientCredits 
                      ? 'bg-red-50 border border-red-100' 
                      : 'bg-green-50 border border-green-100'
                  }`}>
                    <div className="flex gap-3 items-center">
                      {metrics.insufficientCredits ? (
                        <AlertTriangle className="w-8 h-8 text-red-500" />
                      ) : (
                        <CheckCircle className="w-8 h-8 text-green-500" />
                      )}
                      <div>
                        <h4 className={`font-semibold ${
                          metrics.insufficientCredits ? 'text-red-700' : 'text-green-700'
                        }`}>
                          {metrics.insufficientCredits 
                            ? 'Insufficient Credits' 
                            : 'Sufficient Credits'}
                        </h4>
                        <p className="text-sm text-gray-600">
                          {metrics.insufficientCredits 
                            ? `You need ${metrics.remainingToBlast - metrics.possibleBlasts} more credits` 
                            : `You have enough credits for this blast`}
                        </p>
                      </div>
                    </div>
                  </div>

                  {/* Stats Grid */}
                  <div className="grid grid-cols-2 gap-3 sm:gap-4">
                    <div className="p-4 bg-indigo-50 rounded-xl">
                      <p className="mb-1 text-sm text-gray-600">Filtered Subscribers</p>
                      <p className="text-xl font-bold text-indigo-600">
                        {metrics.filteredSubscribers.toLocaleString()}
                      </p>
                    </div>
                    <div className="p-4 bg-purple-50 rounded-xl">
                      <p className="mb-1 text-sm text-gray-600">Remaining Subscribers</p>
                      <p className="text-xl font-bold text-purple-600">
                        {metrics.remainingToBlast.toLocaleString()}
                      </p>
                    </div>
                    <div className="p-4 bg-blue-50 rounded-xl">
                      <p className="mb-1 text-sm text-gray-600">Able to Send</p>
                      <p className="text-xl font-bold text-blue-600">
                        {metrics.possibleBlasts.toLocaleString()}
                      </p>
                    </div>
                    <div className="p-4 bg-green-50 rounded-xl">
                      <p className="mb-1 text-sm text-gray-600">Estimated Cost</p>
                      <p className="text-xl font-bold text-green-600">
                        GHC {metrics.estimatedCost.toFixed(2)}
                      </p>
                    </div>
                  </div>

                  {/* Blast History */}
                  <div className="p-3 space-y-4 bg-gray-50 rounded-xl border border-gray-100 sm:p-4">
                    <div className="flex gap-2 items-center mb-2">
                      <MessageSquare className="w-5 h-5 text-gray-600" />
                      <h4 className="font-semibold text-gray-700">Blast Progress</h4>
                    </div>
                    
                    <div className="grid grid-cols-2 gap-4">
                      <div className="p-3 bg-white rounded-lg">
                        <p className="mb-1 text-sm text-gray-600">Already Blasted</p>
                        <p className="text-xl font-bold text-gray-700">
                          {(metrics.filteredSubscribers - metrics.remainingToBlast).toLocaleString()} of {metrics.filteredSubscribers.toLocaleString()}
                        </p>
                      </div>
                      <div className="p-3 bg-white rounded-lg">
                        <p className="mb-1 text-sm text-gray-600">SMS Credits</p>
                        <p className="text-xl font-bold text-indigo-600">
                          {totalSmsLeads.toLocaleString() || 0} credits
                        </p>
                      </div>
                    </div>

                    <div className="space-y-2">
                      <div className="flex justify-between text-sm">
                        <span className="text-gray-600">Campaign Completion</span>
                        <span className="font-medium text-gray-900">
                          {(metrics.filteredSubscribers - metrics.remainingToBlast).toLocaleString()} / {metrics.filteredSubscribers.toLocaleString()}
                        </span>
                      </div>
                      <div className="overflow-hidden h-2 bg-gray-200 rounded-full">
                        <div 
                          className="h-full bg-indigo-500 rounded-full transition-all duration-300"
                          style={{ 
                            width: `${((metrics.filteredSubscribers - metrics.remainingToBlast) / metrics.filteredSubscribers) * 100}%` 
                          }}
                        />
                      </div>
                    </div>
                  </div>

                  {/* Credits Bar and Blast Controls */}
                  <div className="space-y-3 sm:space-y-4">
                    <div className="space-y-3">
                      {/* Sender Selection */}
                      <div className="space-y-2">
                        <div className="flex gap-2 items-center mb-1">
                          <AtSign className="w-4 h-4 text-gray-500" />
                          <span className="text-sm font-medium text-gray-700">Select Sender ID</span>
                        </div>
                        <FormControl fullWidth>
                          <Select
                            value={selectedSender}
                            onChange={(e) => setSelectedSender(e.target.value)}
                            displayEmpty
                            size="small"
                            sx={{
                              borderRadius: '12px',
                              '& .MuiOutlinedInput-notchedOutline': {
                                borderColor: '#E5E7EB',
                              },
                              '&:hover .MuiOutlinedInput-notchedOutline': {
                                borderColor: '#FF6C6C',
                              },
                              '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                                borderColor: '#FF6C6C',
                              },
                              '& .MuiSelect-select': {
                                padding: '12px',
                              }
                            }}
                          >
                            <MenuItem value="" disabled>
                              <span className="text-gray-400">Choose a sender for your SMS blast</span>
                            </MenuItem>
                            {senderOptions.map((sender) => (
                              <MenuItem 
                                key={sender.id} 
                                value={sender.name}
                                sx={{
                                  borderRadius: '8px',
                                  margin: '4px',
                                  '&:hover': {
                                    backgroundColor: '#FFF5F5',
                                  },
                                  '&.Mui-selected': {
                                    backgroundColor: '#FFF5F5',
                                    '&:hover': {
                                      backgroundColor: '#FFE5E5',
                                    },
                                  },
                                }}
                              >
                                <div className="py-1">
                                  <div className="font-medium text-gray-900">{sender.name}</div>
                                </div>
                              </MenuItem>
                            ))}
                          </Select>
                        </FormControl>
                      </div>

                      {!metrics.insufficientCredits && (
                        <FormControlLabel
                          control={
                            <Checkbox
                              checked={blastToAll}
                              onChange={(e) => {
                                setBlastToAll(e.target.checked);
                                if (e.target.checked) {
                                  setCustomBlastCount('');
                                }
                              }}
                              disabled={!selectedSender} // Disable if no sender selected
                              sx={{
                                '&.Mui-checked': {
                                  color: '#FF6C6C',
                                },
                              }}
                            />
                          }
                          label={
                            <span className={!selectedSender ? 'text-gray-400' : 'text-gray-900'}>
                              Blast to all subscribers
                            </span>
                          }
                        />
                      )}

                      {(!blastToAll || metrics.insufficientCredits) && (
                        <div className="space-y-4">
                          <TextField
                            fullWidth
                            label="Number of recipients"
                            value={customBlastCount}
                            onChange={(e) => {
                              const value = e.target.value;
                              if (value === '' || (parseInt(value) >= 0 && parseInt(value) <= metrics.possibleBlasts)) {
                                setCustomBlastCount(value);
                              }
                            }}
                            type="number"
                            error={customBlastCount && !isValidBlastCount()}
                            helperText={
                              customBlastCount && !isValidBlastCount()
                                ? `Maximum allowed recipients: ${metrics.possibleBlasts}`
                                : ''
                            }
                            InputProps={{
                              inputProps: { 
                                min: 0,
                                max: metrics.possibleBlasts
                              }
                            }}
                            sx={{
                              '& .MuiOutlinedInput-root': {
                                borderRadius: '12px',
                                '&.Mui-focused fieldset': {
                                  borderColor: '#FF6C6C',
                                },
                              },
                              '& .MuiInputLabel-root.Mui-focused': {
                                color: '#FF6C6C',
                              },
                            }}
                          />

                          <div className="space-y-2">
                            <div className="flex justify-between text-sm">
                              <span className="text-gray-600">Recipients Selection</span>
                              <span className="font-medium text-gray-900">
                                {customBlastCount || '0'} / {metrics.possibleBlasts}
                              </span>
                            </div>
                            
                            <Slider
                              value={parseInt(customBlastCount) || 0}
                              onChange={(_, value) => setCustomBlastCount(value.toString())}
                              min={0}
                              max={metrics.possibleBlasts}
                              sx={{
                                color: '#FF6C6C',
                                height: 8,
                                '& .MuiSlider-thumb': {
                                  height: 24,
                                  width: 24,
                                  backgroundColor: '#fff',
                                  border: '2px solid #FF6C6C',
                                  '&:focus, &:hover, &.Mui-active, &.Mui-focusVisible': {
                                    boxShadow: 'inherit',
                                  },
                                  '&:before': {
                                    display: 'none',
                                  },
                                },
                                '& .MuiSlider-track': {
                                  height: 8,
                                  borderRadius: 4,
                                },
                                '& .MuiSlider-rail': {
                                  height: 8,
                                  borderRadius: 4,
                                  backgroundColor: '#E5E7EB',
                                },
                              }}
                            />
                          </div>
                        </div>
                      )}
                    </div>
                  </div>

                  {/* Action Buttons */}
                  <div className="pt-4 space-y-3">
                    <Button
                      variant="contained"
                      fullWidth
                      className="py-3 sm:py-4"
                      disabled={
                        !selectedSender ||
                        metrics.possibleBlasts === 0 ||
                        (!blastToAll && !isValidBlastCount())
                      }
                      onClick={() => setIsBlastModalOpen(true)}
                      sx={{
                        backgroundColor: '#FF6C6C',
                        borderRadius: '12px',
                        padding: '12px',
                        '&:hover': {
                          backgroundColor: '#ff5555'
                        },
                        '&.Mui-disabled': {
                          backgroundColor: '#E5E7EB',
                          color: '#9CA3AF'
                        }
                      }}
                    >
                      Send SMS Blast
                    </Button>
                    
                    {metrics.insufficientCredits && (
                      <Button
                      onClick={()=>navigate("/pricing")}
                        variant="outlined"
                        fullWidth
                        className="py-3 sm:py-4"
                        startIcon={<Zap className="w-4 h-4" />}
                        sx={{
                          borderColor: '#FF6C6C',
                          color: '#FF6C6C',
                          borderRadius: '12px',
                          padding: '12px',
                          '&:hover': {
                            borderColor: '#ff5555',
                            color: '#ff5555',
                            backgroundColor: '#FFF5F5'
                          }
                        }}
                      >
                        Buy More Credits
                      </Button>
                    )}
                  </div>
                </div>
              </motion.div>
            </div>
          </div>
        </div>
      </div>
      <BlastModal
        open={isBlastModalOpen}
        onClose={() => setIsBlastModalOpen(false)}
        blastData={{
          recipientCount: blastToAll 
            ? Math.min(metrics.possibleBlasts, metrics.filteredSubscribers)
            : parseInt(customBlastCount),
          totalSmsLeads,
          costPerSMS: getPlanPerAmount(userPlan),
          isBlastToAll: blastToAll,
          sender: senderOptions.find(s => s.id === selectedSender)?.name
        }}
        onConfirm={handleBlastConfirm}
      />
      <TestSmsModal
        open={isTestSmsModalOpen}
        onClose={() => setIsTestSmsModalOpen(false)}
        testData={{
          phoneNumbers: getFormattedPhoneNumbersArray(),
          dealTitle: dealPreview && dealPreview.title,
          sender: senderOptions.find(s => s.id === selectedSender)?.name,
          senderOptions: senderOptions,
          previewMessage: `${campaignData && campaignData.sms_content}`,
          shorten_url: `${campaignData && campaignData.shorten_url}`,
          campaignSlug: campaignData && campaignData.campaign_slug
        }}
        onConfirm={resetTestPhoneNumbers}
        setIsTestSmsModalOpen={setIsTestSmsModalOpen}
        sender={selectedSender}
      />
      <BlastProcessing
        open={isProcessingModalOpen}
        onClose={() => setIsProcessingModalOpen(false)}
        onSuccessClose={() => setIsProcessingModalOpen(false)}
        recipientCount={processingRecipientCount}
        isSuccess={blastSuccess}
      />
      {isFreeTrial() && <SubscriptionPrompt />}
    </div>
  );
};

export default BlastCampaign;